import React, { useState, useEffect, useMemo } from 'react';
import countryList, { Country } from 'react-select-country-list';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// No mock data - using database data only

interface FormData {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  type: string;
  status: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  type?: string;
  status?: string;
}

const CustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const isEditMode = Boolean(id);
  
  // Get country options using react-select-country-list
  const countryOptions = useMemo(() => countryList().getData(), []);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United Kingdom', // Default to United Kingdom
    type: 'Business',
    status: 'Active',
    notes: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCustomer = async () => {
      if (!isEditMode) return;
      
      setLoading(true);
      try {
        // Fetch customer data from the backend API
        const response = await api.get(`/customers/${id}`);
        
        if (response.data && response.data.success) {
          const customerData = response.data.data;
          
          setFormData({
            name: customerData.company_name || '',
            contactName: customerData.contact_name || '',
            email: customerData.email || '',
            phone: customerData.phone || '',
            address: customerData.address || '',
            city: customerData.city || '',
            state: customerData.state || '',
            postalCode: customerData.postal_code || '',
            country: customerData.country || '',
            type: customerData.type || 'Business',
            status: customerData.is_active ? 'Active' : 'Inactive',
            notes: customerData.notes || '',
          });
        } else {
          // If API call succeeds but returns no data, show error
          console.warn('No customer data received from API');
          setSubmitError('Customer not found');
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
        setSubmitError('Error loading customer data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated) {
      fetchCustomer();
    }
  }, [authLoading, isAuthenticated, id, isEditMode]);
  
  // Handle both input changes and select changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | any) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined,
        });
      }
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (formData.email.trim() && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!formData.type) {
      newErrors.type = 'Customer type is required';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    setSubmitError(null);
    
    try {
      // Prepare customer data for the API
      const customerData = {
        company_name: formData.name,
        contact_name: formData.contactName || formData.name, // Use company name as fallback if contact name is empty
        email: formData.email.trim() || null, // Send null if email is empty
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        notes: formData.notes,
        is_active: formData.status === 'Active'
      };
      
      // Call the backend API to create or update the customer
      const response = isEditMode
        ? await api.put(`/customers/${id}`, customerData)
        : await api.post('/customers', customerData);
      
      navigate('/customers');
    } catch (error) {
      console.error('Error saving customer:', error);
      setSubmitError('An error occurred while saving the customer. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/customers');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Edit Customer' : 'Add Customer'}
        </Typography>
      </Box>
      
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              label="Company Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />
            
            <TextField
              label="Contact Person"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              fullWidth
              error={!!errors.contactName}
              helperText={errors.contactName}
            />
            
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
            />
            
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.phone}
              helperText={errors.phone}
            />
            
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.address}
              helperText={errors.address}
            />
            
            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              fullWidth
              error={!!errors.city}
              helperText={errors.city}
            />
            
            <TextField
              label="State/Province"
              name="state"
              value={formData.state}
              onChange={handleChange}
              fullWidth
              error={!!errors.state}
              helperText={errors.state}
            />
            
            <TextField
              label="Postal Code"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              fullWidth
              error={!!errors.postalCode}
              helperText={errors.postalCode}
            />
            
            <FormControl fullWidth error={!!errors.country}>
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={formData.country}
                onChange={handleChange}
                label="Country"
              >
                {countryOptions.map((country: Country) => (
                  <MenuItem key={country.value} value={country.label}>
                    {country.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
            </FormControl>
            
            <FormControl fullWidth required error={!!errors.type}>
              <InputLabel>Customer Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Customer Type"
              >
                <MenuItem value="Business">Business</MenuItem>
                <MenuItem value="Individual">Individual</MenuItem>
              </Select>
              {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>
            
            <FormControl fullWidth required error={!!errors.status}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
            
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ gridColumn: { md: '1 / span 2' } }}
            />
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Saving...' : (isEditMode ? 'Update Customer' : 'Create Customer')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerForm;
